import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Package, Send, Save, ArrowLeft, ClipboardList, Home, Settings, Check, Layers, Plus, X, FileText, Calculator, RefreshCw, User } from 'lucide-react';
import { format } from 'date-fns';

const enconOrange = '#ee7203';
const warehouseBlue = '#6366f1';

// Simple Header Component
const SimpleHeader = ({ navigate }) => (
  <div className="bg-white border-b border-gray-200 px-4 py-3">
    <div className="flex items-center justify-between max-w-[1400px] mx-auto">
      <button
        onClick={() => navigate('/settings')}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        title="Settings"
      >
        <Settings className="w-6 h-6" style={{ color: enconOrange }} />
      </button>
      
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        title="Home"
      >
        <Home className="w-6 h-6" style={{ color: enconOrange }} />
        <span className="font-semibold text-lg" style={{ color: enconOrange }}>Home</span>
      </button>
      
      <button
        onClick={() => window.history.back()}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1"
        title="Back"
      >
        <ArrowLeft className="w-5 h-5" style={{ color: enconOrange }} />
        <span className="text-sm font-semibold hidden sm:inline" style={{ color: enconOrange }}>Back</span>
      </button>
    </div>
  </div>
);

// Get count type description
const getCountTypeDescription = (count) => {
  const status = count?.status || '';
  const countType = count?.count_type || '';
  const supplierName = count?.supplier_name || '';
  
  // Check if supplier name contains count type hints
  const nameHasSecond = supplierName.toLowerCase().includes('second');
  const nameHasDiscrepancy = supplierName.toLowerCase().includes('discrepancy');
  
  if (status === 'discrepancy_second_count' || countType === 'discrepancy_second') {
    return 'Discrepancy Second Count - Re-count items with discrepancies';
  }
  if (status === 'discrepancy_pending' || countType === 'discrepancy' || nameHasDiscrepancy) {
    return 'Discrepancy Count - Verify items flagged with discrepancies';
  }
  if (status === 'second_count_pending' || countType === 'second' || status.includes('second') || nameHasSecond) {
    return 'Second Count - Independent verification count';
  }
  return 'First Count - Initial physical inventory count';
};

// Notes Modal Component
const NotesModal = ({ isOpen, onClose, notes, onSave, productName }) => {
  const [localNotes, setLocalNotes] = useState(notes || '');
  const textareaRef = useRef(null);

  useEffect(() => {
    setLocalNotes(notes || '');
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, notes]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(localNotes);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-4">
      <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-lg text-gray-900">Notes</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-500 mb-2 truncate">{productName}</p>
          <Textarea
            ref={textareaRef}
            value={localNotes}
            onChange={(e) => setLocalNotes(e.target.value)}
            placeholder="Add notes for this product..."
            className="min-h-[150px] text-base border-2 focus:border-indigo-400"
            style={{ fontSize: '16px' }}
          />
        </div>
        <div className="flex gap-3 p-4 border-t">
          <Button variant="outline" onClick={onClose} className="flex-1 h-12">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            className="flex-1 h-12"
            style={{ backgroundColor: warehouseBlue }}
          >
            Save Notes
          </Button>
        </div>
      </div>
    </div>
  );
};

// Product Count Card Component - Mobile optimized with multi-entry system
const ProductCountCard = ({ 
  product, 
  index, 
  onCountChange, 
  onNotesChange,
  isActive,
  onActivate,
  cardRef,
  isSecondCount,  // Prop to indicate if this is a second count
  isFirstCount,   // Prop to indicate if this is a first count (hide stock details)
  isShelfLife,    // Prop to indicate this is a Shelf Life count (show Expiry Date)
  onExpiryDateChange
}) => {
  const [entries, setEntries] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const inputRef = useRef(null);
  
  const hasCounted = product.physical_count !== null && product.physical_count !== undefined;

  // Initialize entries from existing count
  useEffect(() => {
    if (hasCounted && entries.length === 0) {
      // If there's an existing count but no entries, create one entry with that value
      setEntries([product.physical_count]);
    }
  }, [product.physical_count, hasCounted]);

  // Calculate total from entries
  const calculateTotal = (entryList) => {
    return entryList.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  // Evaluate mathematical expression
  const evaluateExpression = (expr) => {
    try {
      // Only allow numbers and basic operators
      const sanitized = expr.replace(/[^0-9+\-*/().]/g, '');
      if (!sanitized) return null;
      // eslint-disable-next-line no-eval
      const result = eval(sanitized);
      return typeof result === 'number' && !isNaN(result) ? result : null;
    } catch {
      return null;
    }
  };

  // Handle adding/updating entry
  const handleAddEntry = useCallback(() => {
    if (!inputValue.trim()) return;

    const evaluated = evaluateExpression(inputValue);
    if (evaluated === null) {
      toast.error('Invalid number or expression');
      return;
    }

    let newEntries;
    if (editingIndex !== null) {
      // Update existing entry
      newEntries = [...entries];
      newEntries[editingIndex] = evaluated;
      setEditingIndex(null);
    } else {
      // Add new entry
      newEntries = [...entries, evaluated];
    }

    setEntries(newEntries);
    setInputValue('');
    
    // Update the total count
    const total = calculateTotal(newEntries);
    onCountChange(product.id, product.code || product.product_code, total.toString());
    
    // Keep focus on input
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [inputValue, entries, editingIndex, product.id, product.code, product.product_code, onCountChange]);

  // Handle key press (Enter to add)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEntry();
    }
  };

  // Handle clicking on an entry to edit
  const handleEntryClick = (idx) => {
    setInputValue(entries[idx].toString());
    setEditingIndex(idx);
    inputRef.current?.focus();
  };

  // Handle removing an entry
  const handleRemoveEntry = (idx, e) => {
    e.stopPropagation();
    const newEntries = entries.filter((_, i) => i !== idx);
    setEntries(newEntries);
    const total = calculateTotal(newEntries);
    onCountChange(product.id, product.code || product.product_code, total > 0 ? total.toString() : '');
    if (editingIndex === idx) {
      setEditingIndex(null);
      setInputValue('');
    }
  };

  // Handle notes save
  const handleNotesSave = (newNotes) => {
    onNotesChange(product.id, product.code || product.product_code, newNotes);
  };

  const total = calculateTotal(entries);

  return (
    <>
      <div
        ref={cardRef}
        data-testid={`product-card-${product.id}`}
        className={`rounded-xl border-2 transition-all ${
          hasCounted
            ? 'bg-green-50 border-green-400'
            : isActive
            ? 'bg-indigo-50 border-indigo-400'
            : 'bg-gray-50 border-gray-200'
        }`}
        onClick={() => !isActive && onActivate()}
        style={{ scrollMarginTop: '180px' }}
      >
        {/* Product Header - Always visible */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-white px-2 py-1 rounded" style={{ backgroundColor: warehouseBlue }}>
                #{index + 1}
              </span>
              {hasCounted && <Check className="w-5 h-5 text-green-600" />}
            </div>
            {/* Total Display */}
            <div className="flex items-center gap-2">
              {product.notes && (
                <FileText className="w-4 h-4 text-blue-500" />
              )}
              <div className="text-right">
                <span className="text-xs text-gray-500">Total</span>
                <p className="text-xl font-bold" style={{ color: hasCounted ? '#22c55e' : warehouseBlue }}>
                  {total || '—'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Product Code */}
          <p className="text-xs font-mono text-indigo-600 mt-2 bg-indigo-50 px-2 py-1 rounded inline-block">
            {product.code || product.product_code || 'No code'}
          </p>
          
          {/* Product Name/Description */}
          <p className="text-lg font-bold text-gray-900 mt-2 leading-tight">
            {product.name || product.description || product.product_name || product.code || 'No description'}
          </p>

          {/* Expiry Date — Shelf Life counts only. Enterable at any point,
              regardless of whether counting has started, since recording
              this date is the main purpose of a Shelf Life count. */}
          {isShelfLife && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <label className="text-xs font-semibold text-red-600 block mb-1">Expiry Date</label>
              <Input
                type="date"
                value={product.expiry_date || ''}
                onChange={(e) => onExpiryDateChange(product.id, product.code || product.product_code, e.target.value)}
                className="w-full h-11 text-base border-2 border-red-300 focus:border-red-500"
                style={{ fontSize: '16px' }}
                data-testid={`expiry-date-input-${product.id}`}
              />
            </div>
          )}
          
          {/* Unit only — On Hand is hidden from the warehouse view per
              user request: warehouse staff shouldn't see what the system
              expects to be on hand (would bias the physical count). */}
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span>Unit: <span className="font-semibold text-gray-700">{product.stock_unit || 'ea'}</span></span>
          </div>
          
          {/* Original Count Data block removed per user request — warehouse
              view for 2nd/3rd+ counts and scheduled counts should never
              show original counted / original on hand. */}
        </div>

        {/* Entry Section - Only when active or has entries */}
        {(isActive || entries.length > 0) && (
          <div className="p-4">
            {/* Entries Display */}
            {entries.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {entries.map((entry, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleEntryClick(idx)}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 ${
                      editingIndex === idx
                        ? 'bg-indigo-500 text-white ring-2 ring-indigo-300'
                        : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-indigo-400'
                    }`}
                  >
                    {entry}
                    <button
                      onClick={(e) => handleRemoveEntry(idx, e)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </button>
                ))}
              </div>
            )}

            {/* Input Row */}
            {isActive && (
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9+\-*/.]*"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={editingIndex !== null ? 'Edit entry...' : 'Enter count or calc (e.g. 25*5)'}
                    className="h-14 text-xl font-bold text-center border-2 focus:border-indigo-400 pr-10"
                    style={{ fontSize: '16px' }}
                    autoComplete="off"
                  />
                  {inputValue && inputValue.includes('*') || inputValue.includes('+') || inputValue.includes('-') || inputValue.includes('/') ? (
                    <Calculator className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-400" />
                  ) : null}
                </div>
                
                {/* Add Button */}
                <Button
                  onClick={handleAddEntry}
                  className="h-14 w-14 p-0"
                  style={{ backgroundColor: warehouseBlue }}
                  disabled={!inputValue.trim()}
                >
                  <Plus className="w-6 h-6" />
                </Button>
                
                {/* Notes Button */}
                <Button
                  onClick={() => setShowNotesModal(true)}
                  variant="outline"
                  className={`h-14 w-14 p-0 ${product.notes ? 'border-blue-400 bg-blue-50' : ''}`}
                >
                  <FileText className={`w-5 h-5 ${product.notes ? 'text-blue-500' : 'text-gray-400'}`} />
                </Button>
              </div>
            )}

            {/* Tap to count hint when not active */}
            {!isActive && entries.length === 0 && (
              <button
                onClick={onActivate}
                className="w-full py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors"
              >
                Tap to enter count
              </button>
            )}
          </div>
        )}

        {/* Tap to expand when collapsed and no entries */}
        {!isActive && entries.length === 0 && (
          <div className="p-4 text-center">
            <span className="text-sm text-gray-400">Tap to count →</span>
          </div>
        )}
      </div>

      {/* Notes Modal */}
      <NotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        notes={product.notes}
        onSave={handleNotesSave}
        productName={product.name || product.code}
      />
    </>
  );
};

const PICountsWarehouse = ({ user }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCount, setSelectedCount] = useState(null);
  const [countProducts, setCountProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sendingToOffice, setSendingToOffice] = useState(false);
  const [activeProductId, setActiveProductId] = useState(null);
  const [refreshingM4, setRefreshingM4] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving', 'saved', 'error'
  const [showNameModal, setShowNameModal] = useState(false);
  const [countedByName, setCountedByName] = useState('');
  
  // Combined count tabs state
  const [activeSupplierTab, setActiveSupplierTab] = useState(null);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [isCombinedCount, setIsCombinedCount] = useState(false);
  
  // Refs for scrolling and auto-save
  const productRefs = useRef({});
  const autoSaveTimeoutRef = useRef(null);
  const pendingSaveRef = useRef(false);

  useEffect(() => {
    loadWarehouseCounts();
  }, []);

  // Prevent zoom on input focus (iOS)
  useEffect(() => {
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }
    return () => {
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      }
    };
  }, [view]);

  const loadWarehouseCounts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/pi-counts/warehouse`);
      setCounts(response.data.counts || []);
    } catch (error) {
      console.error('Error loading warehouse counts:', error);
      toast.error('Failed to load counts');
    } finally {
      setLoading(false);
    }
  };

  const openCount = async (count) => {
    setSelectedCount(count);
    
    const combined = count.is_combined && count.supplier_groups?.length > 1;
    setIsCombinedCount(combined);
    
    if (combined) {
      setSupplierGroups(count.supplier_groups);
      setActiveSupplierTab(count.supplier_groups[0].supplier_group_id);
    } else {
      setSupplierGroups([]);
      setActiveSupplierTab(null);
    }
    
    try {
      const response = await api.get(`${API}/pi-counts/${count.id}/products`);
      const products = response.data.products || [];
      setCountProducts(products);
      setView('count');
      // Set first product as active
      if (products.length > 0) {
        setActiveProductId(products[0].id);
      }
    } catch (error) {
      console.error('Error loading count products:', error);
      toast.error('Failed to load count products');
    }
  };
  
  const filteredProducts = isCombinedCount && activeSupplierTab
    ? countProducts.filter(p => p.supplier_group_id === activeSupplierTab)
    : countProducts;
  
  const getSupplierGroupProgress = (supplierGroupId) => {
    const groupProducts = countProducts.filter(p => p.supplier_group_id === supplierGroupId);
    const counted = groupProducts.filter(p => p.physical_count !== null && p.physical_count !== undefined).length;
    return { counted, total: groupProducts.length };
  };

  // Auto-save function - saves to backend
  const autoSaveToBackend = useCallback(async (products) => {
    if (!selectedCount || !products || products.length === 0) return;
    
    try {
      setAutoSaveStatus('saving');
      pendingSaveRef.current = true;
      
      await api.put(`${API}/pi-counts/${selectedCount.id}/products`, {
        products: products.map(p => ({
          id: p.id,
          product_id: p.product_id || p.id,
          code: p.code || p.product_code,
          product_code: p.product_code || p.code,
          name: p.name || p.description || p.product_name,
          description: p.description || p.name || p.product_name,
          product_name: p.product_name || p.name || p.description,
          on_hand_quantity: p.on_hand_quantity,
          on_hand_balance: p.on_hand_balance || p.on_hand_quantity,
          stock_unit: p.stock_unit,
          physical_count: p.physical_count,
          on_the_road: p.on_the_road || 0,
          discrepancy: p.discrepancy || 0,
          notes: p.notes || '',
          expiry_date: p.expiry_date || null,
          original_on_hand: p.original_on_hand,
          original_physical_count: p.original_physical_count,
          original_discrepancy: p.original_discrepancy,
          original_count_date: p.original_count_date,
          count_time_on_hand: p.count_time_on_hand,
          supplier_group_id: p.supplier_group_id,
          supplier_group_name: p.supplier_group_name
        }))
      });
      
      setAutoSaveStatus('saved');
      pendingSaveRef.current = false;
      
      // Clear saved status after 2 seconds
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveStatus('error');
      pendingSaveRef.current = false;
    }
  }, [selectedCount]);

  // Debounced auto-save - waits 500ms after last change before saving
  const debouncedAutoSave = useCallback((products) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveToBackend(products);
    }, 500);
  }, [autoSaveToBackend]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const handleCountChange = (productId, productCode, value) => {
    setCountProducts(prev => {
      const updated = prev.map(p => {
        // Match by id first, then fall back to product code for uniqueness
        const isMatch = productId ? p.id === productId : (p.code || p.product_code) === productCode;
        if (isMatch) {
          const physicalCount = value === '' ? null : parseFloat(value) || 0;
          const onHandQty = p.on_hand_quantity ?? p.on_hand_balance ?? p.free_stock ?? 0;
          const onTheRoad = p.on_the_road || 0;
          const discrepancy = physicalCount !== null ? (physicalCount + onTheRoad) - onHandQty : 0;
          
          return {
            ...p,
            physical_count: physicalCount,
            discrepancy: discrepancy
          };
        }
        return p;
      });
      
      // Trigger auto-save with updated products
      debouncedAutoSave(updated);
      
      return updated;
    });
  };

  const handleNotesChange = (productId, productCode, value) => {
    setCountProducts(prev => {
      const updated = prev.map(p => {
        // Match by id first, then fall back to product code for uniqueness
        const isMatch = productId ? p.id === productId : (p.code || p.product_code) === productCode;
        return isMatch ? { ...p, notes: value } : p;
      });
      
      // Trigger auto-save with updated products
      debouncedAutoSave(updated);
      
      return updated;
    });
  };

  const handleExpiryDateChange = (productId, productCode, value) => {
    setCountProducts(prev => {
      const updated = prev.map(p => {
        const isMatch = productId ? p.id === productId : (p.code || p.product_code) === productCode;
        return isMatch ? { ...p, expiry_date: value || null } : p;
      });
      debouncedAutoSave(updated);
      return updated;
    });
  };

  // Scroll to next product
  const scrollToNextProduct = () => {
    const currentIndex = filteredProducts.findIndex(p => p.id === activeProductId);
    if (currentIndex < filteredProducts.length - 1) {
      const nextProduct = filteredProducts[currentIndex + 1];
      setActiveProductId(nextProduct.id);
      const ref = productRefs.current[nextProduct.id];
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Handle product activation and scroll
  const handleProductActivate = (productId) => {
    setActiveProductId(productId);
    const ref = productRefs.current[productId];
    if (ref) {
      setTimeout(() => {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const saveProgress = async () => {
    if (!selectedCount) return;
    
    setSaving(true);
    try {
      await api.put(`${API}/pi-counts/${selectedCount.id}/products`, {
        products: countProducts.map(p => ({
          // Preserve all original product data
          id: p.id,
          product_id: p.product_id || p.id,
          code: p.code || p.product_code,
          product_code: p.product_code || p.code,
          name: p.name || p.description || p.product_name,
          description: p.description || p.name || p.product_name,
          product_name: p.product_name || p.name || p.description,
          on_hand_quantity: p.on_hand_quantity,
          on_hand_balance: p.on_hand_balance || p.on_hand_quantity,
          stock_unit: p.stock_unit,
          // Count data
          physical_count: p.physical_count,
          on_the_road: p.on_the_road || 0,
          discrepancy: p.discrepancy || 0,
          notes: p.notes || '',
          expiry_date: p.expiry_date || null,
          // Preserve original count data for second counts
          original_on_hand: p.original_on_hand,
          original_physical_count: p.original_physical_count,
          original_discrepancy: p.original_discrepancy,
          original_count_date: p.original_count_date,
          count_time_on_hand: p.count_time_on_hand,
          // Supplier group data for combined counts
          supplier_group_id: p.supplier_group_id,
          supplier_group_name: p.supplier_group_name
        }))
      });
      toast.success('Progress saved');
      scrollToNextProduct();
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  // Refresh M4 On Hand Data
  const refreshM4Data = async () => {
    if (!selectedCount) return;
    
    try {
      setRefreshingM4(true);
      const response = await api.post(`${API}/pi-counts/${selectedCount.id}/refresh-m4-data`);
      toast.success(response.data.message || 'M4 data refreshed successfully');
      // Reload count products to get updated on_hand values
      const productsRes = await api.get(`${API}/pi-counts/${selectedCount.id}/products`);
      setCountProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error refreshing M4 data:', error);
      toast.error(error.response?.data?.detail || 'Failed to refresh M4 data');
    } finally {
      setRefreshingM4(false);
    }
  };

  const sendToOffice = () => {
    if (!selectedCount) return;
    // Pre-fill with the logged-in user's name so the warehouse user only
    // needs to confirm (or overwrite) before sending. Prevents the modal
    // from blocking a send simply because no name was typed.
    const defaultName = (user?.full_name || user?.username || '').trim();
    setCountedByName(defaultName);
    setShowNameModal(true);
  };

  const confirmReturnToOffice = async () => {
    if (!selectedCount) return;
    const name = countedByName.trim();
    if (!name) {
      toast.error('Please enter the name of the person who counted this');
      return;
    }
    setShowNameModal(false);
    setSendingToOffice(true);
    try {
      await saveProgress();
      await api.put(`${API}/pi-counts/${selectedCount.id}/return-from-warehouse`, {
        counted_by_name: name
      });
      toast.success('Count sent to office');
      setView('list');
      setSelectedCount(null);
      setCountProducts([]);
      setActiveProductId(null);
      await loadWarehouseCounts();
    } catch (error) {
      console.error('Error sending to office:', error);
      toast.error(error.response?.data?.detail || 'Failed to send to office');
    } finally {
      setSendingToOffice(false);
    }
  };

  const closeCountView = async () => {
    if (countProducts.some(p => p.physical_count !== null)) {
      await saveProgress();
    }
    setView('list');
    setSelectedCount(null);
    setCountProducts([]);
    setActiveProductId(null);
    setIsCombinedCount(false);
    setSupplierGroups([]);
    setActiveSupplierTab(null);
  };

  const completedCount = filteredProducts.filter(p => p.physical_count !== null).length;
  const totalFilteredCount = filteredProducts.length;

  const getCountProgress = (count) => {
    const productCounts = count.product_counts || [];
    const total = productCounts.length || count.products_count || 0;
    const counted = productCounts.filter(p => p.physical_count !== null && p.physical_count !== undefined).length;
    return { counted, total };
  };

  // LIST VIEW
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <SimpleHeader navigate={navigate} />
        
        <div className="px-4 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: warehouseBlue }}>
              PI Counts - Warehouse
            </h1>
            <p className="text-gray-600 mt-1">
              Counts sent from office to warehouse
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-lg text-gray-600">Loading counts...</p>
            </div>
          ) : counts.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="py-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-semibold text-gray-700">No counts waiting</p>
                <p className="text-sm text-gray-500 mt-1">Counts sent from office will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {counts.map((count) => {
                const progress = getCountProgress(count);
                const isCombined = count.is_combined && count.supplier_groups?.length > 1;
                const description = getCountTypeDescription(count);
                
                return (
                  <Card
                    key={count.id}
                    data-testid={`count-card-${count.id}`}
                    className="bg-white cursor-pointer hover:shadow-lg transition-shadow border-l-4"
                    style={{ borderLeftColor: warehouseBlue }}
                    onClick={() => openCount(count)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <ClipboardList className="w-8 h-8" style={{ color: warehouseBlue }} />
                        <div className="flex items-center gap-2">
                          {isCombined && (
                            <span className="px-2 py-1 rounded text-[10px] font-bold bg-purple-100 text-purple-700 flex items-center gap-1">
                              <Layers className="w-3 h-3" />
                              {count.supplier_groups.length} TABS
                            </span>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            count.type === 'structural' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {count.type}
                          </span>
                        </div>
                      </div>
                      
                      {count.assigned_to && (
                        <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border-2 border-amber-400" data-testid={`assigned-to-${count.id}`}>
                          <p className="text-xs font-medium text-amber-600 mb-0.5">This count must be counted by:</p>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-amber-600" />
                            <span className="text-base font-bold text-amber-700">{count.assigned_to}</span>
                          </div>
                        </div>
                      )}

                      <h3 className="text-lg font-bold mb-1" style={{ color: warehouseBlue }}>
                        {count.supplier_name}
                      </h3>
                      
                      {/* Count Type Description - Always shown */}
                      <p className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded mb-2 inline-block">
                        {description}
                      </p>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        {count.date && format(new Date(count.date), 'dd MMM yyyy')}
                      </p>
                      
                      <p className="text-xs text-gray-500">
                        Sent: {count.sent_to_warehouse_at && format(new Date(count.sent_to_warehouse_at), 'dd/MM HH:mm')}
                      </p>
                      
                      {isCombined && count.supplier_groups && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          {count.supplier_groups.map((sg, idx) => {
                            const counted = sg.counted || 0;
                            const total = sg.total || 0;
                            const percent = total > 0 ? Math.round((counted / total) * 100) : 0;
                            
                            return (
                              <div key={idx} className="text-xs">
                                <div className="flex justify-between text-gray-600">
                                  <span className="truncate max-w-[150px]">{sg.supplier_name}</span>
                                  <span>{counted}/{total}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-0.5">
                                  <div 
                                    className="h-1.5 rounded-full"
                                    style={{ 
                                      width: `${percent}%`,
                                      backgroundColor: percent === 100 ? '#22c55e' : warehouseBlue
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {!isCombined && progress.total > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold" style={{ color: warehouseBlue }}>
                              {progress.counted}/{progress.total} counted
                            </span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-300"
                              style={{ 
                                width: `${progress.total > 0 ? (progress.counted / progress.total) * 100 : 0}%`,
                                backgroundColor: warehouseBlue 
                              }}
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm font-semibold" style={{ color: warehouseBlue }}>
                          Tap to count →
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // COUNT VIEW - Mobile optimized
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Fixed Header */}
      <div className="bg-white border-b-2 px-4 py-3 sticky top-0 z-20" style={{ borderColor: warehouseBlue }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-xl font-bold truncate" style={{ color: warehouseBlue }}>
              {isCombinedCount ? 'Combined Count' : selectedCount?.supplier_name}
            </h1>
            {isCombinedCount && (
              <span className="px-2 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded flex items-center gap-1 flex-shrink-0">
                <Layers className="w-3 h-3" />
                {supplierGroups.length}
              </span>
            )}
          </div>
          
          {/* Count Type Description */}
          <p className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded mt-1 inline-block">
            {getCountTypeDescription(selectedCount)}
          </p>
          
          {selectedCount?.assigned_to && (
            <div className="mt-1 px-3 py-1 rounded-md bg-amber-50 border border-amber-300 inline-flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-sm font-bold text-amber-700">{selectedCount.assigned_to}</span>
            </div>
          )}
          
          {/* Progress Bar */}
          <div className="flex items-center gap-3 mt-2">
            <span className="text-sm font-semibold whitespace-nowrap" style={{ color: warehouseBlue }}>
              {completedCount}/{totalFilteredCount}
            </span>
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${totalFilteredCount > 0 ? (completedCount / totalFilteredCount) * 100 : 0}%`,
                  backgroundColor: completedCount === totalFilteredCount && totalFilteredCount > 0 ? '#22c55e' : warehouseBlue 
                }}
              />
            </div>
            {/* Auto-save status indicator */}
            {autoSaveStatus && (
              <span className={`text-xs px-2 py-0.5 rounded ml-2 ${
                autoSaveStatus === 'saving' ? 'bg-yellow-100 text-yellow-700' :
                autoSaveStatus === 'saved' ? 'bg-green-100 text-green-700' :
                'bg-red-100 text-red-700'
              }`}>
                {autoSaveStatus === 'saving' ? 'Saving...' :
                 autoSaveStatus === 'saved' ? '✓ Saved' :
                 '⚠ Save failed'}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Combined Count Tabs */}
      {isCombinedCount && supplierGroups.length > 1 && (
        <div className="bg-white border-b px-4 py-2 sticky top-[88px] z-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {supplierGroups.map((sg) => {
                const progress = getSupplierGroupProgress(sg.supplier_group_id);
                const isActive = activeSupplierTab === sg.supplier_group_id;
                const progressPercent = progress.total > 0 ? Math.round((progress.counted / progress.total) * 100) : 0;
                
                return (
                  <button
                    key={sg.supplier_group_id}
                    onClick={() => setActiveSupplierTab(sg.supplier_group_id)}
                    className={`px-3 py-2 rounded-lg border-2 transition-all min-w-[140px] flex-shrink-0 ${
                      isActive 
                        ? 'bg-indigo-50 border-indigo-500' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="text-left">
                      <div className={`font-semibold text-xs truncate ${isActive ? 'text-indigo-700' : 'text-gray-700'}`}>
                        {sg.supplier_name}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <div className="flex-1 bg-gray-200 rounded-full h-1">
                          <div 
                            className="h-1 rounded-full"
                            style={{ 
                              width: `${progressPercent}%`,
                              backgroundColor: progressPercent === 100 ? '#22c55e' : warehouseBlue
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500">
                          {progress.counted}/{progress.total}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Products List */}
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="max-w-[1400px] mx-auto px-4 py-4 space-y-4">
          {filteredProducts.map((product, index) => (
            <ProductCountCard
              key={product.id}
              product={product}
              index={index}
              onCountChange={handleCountChange}
              onNotesChange={handleNotesChange}
              isActive={activeProductId === product.id}
              onActivate={() => handleProductActivate(product.id)}
              cardRef={(el) => { productRefs.current[product.id] = el; }}
              isSecondCount={selectedCount?.is_second_count || selectedCount?.type === 'discrepancy_recount' || selectedCount?.supplier_name?.toLowerCase().includes('second')}
              isFirstCount={!selectedCount?.is_second_count && selectedCount?.type !== 'discrepancy_recount' && !selectedCount?.supplier_name?.toLowerCase().includes('second') && !selectedCount?.supplier_name?.toLowerCase().includes('discrepancy')}
              isShelfLife={selectedCount?.type === 'shelf_life'}
              onExpiryDateChange={handleExpiryDateChange}
            />
          ))}
        </div>
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 px-4 py-3 shadow-lg z-30" style={{ borderColor: warehouseBlue }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-4 gap-2">
            <Button
              onClick={closeCountView}
              variant="outline"
              className="h-12 text-sm font-semibold"
              style={{ borderColor: '#6b7280', color: '#6b7280' }}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Close
            </Button>
            
            <Button
              onClick={saveProgress}
              disabled={saving}
              variant="outline"
              className="h-12 text-sm font-semibold"
              style={{ borderColor: warehouseBlue, color: warehouseBlue }}
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? '...' : 'Save'}
            </Button>
            
            <Button
              onClick={sendToOffice}
              disabled={sendingToOffice || completedCount === 0}
              className="h-12 text-sm font-semibold"
              style={{ backgroundColor: warehouseBlue, color: 'white' }}
            >
              <Send className="w-4 h-4 mr-1" />
              {sendingToOffice ? '...' : 'Send'}
            </Button>
          </div>
        </div>
      </div>

      {/* Counted By Modal */}
      <Dialog open={showNameModal} onOpenChange={setShowNameModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ color: warehouseBlue }}>
              Who counted this?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter the name of the person who carried out this count. It will appear on the count when it returns to the office.
            </p>
            <Input
              data-testid="counted-by-name-input"
              value={countedByName}
              onChange={(e) => setCountedByName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmReturnToOffice(); }}
              placeholder="Enter name, e.g. Paul"
              className="w-full"
              autoFocus
            />
            <div className="flex gap-3 justify-end pt-1">
              <Button
                variant="outline"
                onClick={() => setShowNameModal(false)}
                data-testid="cancel-return-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmReturnToOffice}
                disabled={sendingToOffice || !countedByName.trim()}
                style={{ backgroundColor: warehouseBlue, color: 'white' }}
                data-testid="confirm-return-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingToOffice ? 'Sending...' : 'Send to Office'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PICountsWarehouse;
